<template>
  <div class="settings-insights-tools-list table-wrapper">
    <div class="scrollable">
      <AppTable
        :loading="loading"
        :table-data="tools"
      >
        <template #empty>
          <div class="my-20">No apps or URLs found.</div>
        </template>

        <AppTableColumn
          v-slot="{ row }"
          header="App/URL"
        >
          <div class="d-flex">
            <i
              class="type-icon"
              :class="itemIconClass(row)"
            />
            <div>
              <strong>{{ row.tool_name }}</strong>
              <div class="visible-xs visible-sm text-grey ml-25">
                {{ row.platform_name }}
              </div>
            </div>
          </div>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Classification"
          header-tooltip-type="info-filled"
          header-tooltip-classes="with-tooltip-icon"
          :header-tooltip-size="12"
          :header-tooltip="classificationTooltip"
        >
          <div
            class="settings-insights-tools-list toggle-multiple-options lte-three-elements"
            data-toggle="buttons"
          >
            <label
              v-for="(option, i) in classifications"
              :key="i"
              class="btn btn-toggle-option view-toggle position-relative"
              :class="itemClasses(row, option)"
              :disabled="isItemLoading(row)"
              @click="toggleProductivity(row, option)"
            >
              {{ option.label }}
              <span
                class="text-grey text-center"
                :class="labelClass(row, option)"
              >
                &nbsp;{{ labelContent(row, option) }}
              </span>
            </label>
          </div>
        </AppTableColumn>

        <AppTableColumn
          v-slot="{ row }"
          header="Category"
          header-tooltip-type="info-filled"
          header-tooltip-classes="with-tooltip-icon"
          :header-tooltip-size="12"
          :header-tooltip="categoryTooltip"
        >
          <AppSelect
            class="category-select"
            :value="`${row.category.id}`"
            :options="categoryOptions(row.tool_type)"
            :select2-options="select2Options"
            :disabled="filteredByOccupation"
            :disabled-tooltip="disabledTooltip"
            @input="toggleCategory($event, row)"
          />
        </AppTableColumn>
      </AppTable>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { SETTINGS_INSIGHTS } from '~/app_constants';

const SLIM_WIDTH = 547; // Required to check smaller screen sizes

export default {
  name: 'SettingsInsightsList',
  data: () => ({
    select2Options: Object.freeze({
      allowClear: false,
      multiple: false,
      width: '100%',
      placeholder: 'Select Category',
    }),
    isSmallScreen: false,
    classifications: Object.freeze(SETTINGS_INSIGHTS.productivityOptions),
    classificationTooltip: 'Changes apply to new activity only. Past data is unchanged.',
    categoryTooltip: 'App/URL categories are not job specific and will be applied globally',
    disabledTooltip: 'Categories can only be set globally for entire organization',
  }),
  computed: {
    ...mapState(['query', 'filters', 'jobTypes', 'categories', 'loadingItems', 'tools', 'loading']),
    filteredByOccupation() {
      return !!this.filters?.jobType;
    },
  },
  mounted() {
    this.setupResizeObserver();
  },
  beforeDestroy() {
    this.stopResizeObserver();
  },
  methods: {
    ...mapActions(['updateTool']),
    toggleProductivity(item, option) {
      this.toggleTool(item, { productivity: option.id }, 'productivity', 'Classification updated. Changes apply to new activity only. Past data is unchanged.');
    },
    toggleCategory(id, item) {
      this.toggleTool(item, { category_id: Number(id) || null }, 'category_id');
    },
    toggleTool(item, data, key, successMessage) {
      if (!this.isItemLoading(item)) {
        const itemKey = this.getItemKey(item);
        const params = {
          tool_type: item.tool_type,
          tool_id: item.tool_id,
          occupation_id: this.filters.jobType,
          ...data,
        };

        this.updateTool({ params, itemKey, key, successMessage });
      }
    },
    categoryOptions(type) {
      const defaultName = type === 'url' ? 'Browsing' : 'Uncategorized';
      const array =
        type === 'url' ? this.categories.filter((category) => category.name !== 'Browsing') : this.categories;

      return [{ id: 'null', text: defaultName }, ...array.map(({ id, name: text }) => ({ id, text }))];
    },
    itemIconClass(item) {
      const icon = item.tool_type === 'url' ? 'fa fa-globe' : 'fa fa-laptop';

      return `text-muted ${icon} mr-10`;
    },
    itemClasses(item, option) {
      return {
        active: item.productivity === option.id,
      };
    },
    getItemKey(item) {
      return `${item.tool_type}-${item.tool_id}`;
    },
    isItemLoading(item) {
      return this.loadingItems[this.getItemKey(item)] || false;
    },
    isDefaultOption(row, option) {
      return row.default_productivity && option.id === row.default_productivity;
    },

    labelClass(row, option) {
      if (!row.default_productivity) {
        return '';
      }

      return this.isSmallScreen ? 'default-label-small' : 'default-label';
    },
    labelContent(row, option) {
      if (!row.default_productivity) {
        return '';
      }
      if (this.isDefaultOption(row, option)) {
        return this.isSmallScreen ? '(Default)' : 'Default';
      }

      return this.isSmallScreen ? '' : '•';
    },
    setupResizeObserver() {
      // This ResizeObserver serves one purpose:
      // 1. To move Default label from below classicification for bigger screens
      // to next to classification with paranthesis when displayed for smaller screens
      // NOTE: ResizeObserver will make an initial callback call when initialized.
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(this.onResize);
      });
      this.resizeObserver.observe(this.$el);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    onResize() {
      this.isSmallScreen = this.$el.getBoundingClientRect().width < SLIM_WIDTH;
    },
  },
};
</script>
